import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, MapPin, Mail } from "lucide-react";
import { SITE } from "@/lib/site";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/menu", label: "Menu" },
  { to: "/events", label: "Events" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reservations", label: "Reservations" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const transparent = onHome && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        transparent ? "bg-transparent" : "bg-[hsl(var(--bg))]/95 backdrop-blur border-b border-[hsl(var(--rule))]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
          <span
            className={`display text-2xl md:text-[1.6rem] leading-none ${
              transparent ? "text-[hsl(var(--bg))]" : "text-[hsl(var(--ink))]"
            }`}
          >
            The Barracks <span className="italic text-[hsl(var(--brass))]">Bistro</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide uppercase transition-colors ${
                  transparent
                    ? isActive
                      ? "text-[hsl(var(--brass))]"
                      : "text-[hsl(var(--bg))] hover:text-[hsl(var(--brass))]"
                    : isActive
                    ? "text-[hsl(var(--forest))]"
                    : "text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--forest))]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/reservations"
            data-testid="header-reserve-btn"
            className="btn-brass !py-2.5 !px-5 text-sm"
          >
            Reserve a table
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${transparent ? "text-[hsl(var(--bg))]" : "text-[hsl(var(--ink))]"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[hsl(var(--bg))] border-t border-[hsl(var(--rule))] px-6 py-6"
        >
          <div className="flex flex-col gap-4">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-wide ${
                    isActive ? "text-[hsl(var(--forest))]" : "text-[hsl(var(--ink-soft))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/reservations" className="btn-brass mt-2 justify-center" data-testid="mobile-reserve-btn">
              Reserve a table
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[hsl(var(--forest-2))] text-[hsl(var(--bg))]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="display text-3xl">
            The Barracks <span className="italic text-[hsl(var(--brass))]">Bistro</span>
          </div>
          <p className="mt-4 text-sm max-w-md text-[hsl(var(--bg))]/80">
            Nestled beside the serene Wellington Lake within the historic Madras Regimental Centre,
            The Barracks Bistro offers an exceptional multi-cuisine dining experience inspired by
            military heritage, warm hospitality, and unforgettable flavours.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp"
              className="btn-brass !py-2.5 !px-5 text-sm !bg-[hsl(var(--brass-2))] !border-[hsl(var(--brass-2))] hover:!bg-[hsl(var(--brass))]"
            >
              WhatsApp us
            </a>
            <a
              href={SITE.maps}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-maps"
              className="btn-ghost !py-2.5 !px-5 text-sm !border-[hsl(var(--bg))]/40 !text-[hsl(var(--bg))] hover:!bg-[hsl(var(--bg))]/10"
            >
              <MapPin size={16} /> Find us
            </a>
          </div>
        </div>

        <div>
          <div className="eyebrow !text-[hsl(var(--brass))]">Visit</div>
          <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--bg))]/85">
            <li>{SITE.address}</li>
            {SITE.hours.map((h) => (
              <li key={h.day} className="flex justify-between gap-6">
                <span>{h.day}</span>
                <span className="text-[hsl(var(--bg))]/70">{h.time}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="eyebrow !text-[hsl(var(--brass))]">Contact</div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Mail size={15} className="mt-0.5 text-[hsl(var(--brass))]" />
              <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="hover:text-[hsl(var(--brass))]">
                {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Phone size={15} className="mt-0.5 text-[hsl(var(--brass))]" />
              <a href={`tel:${SITE.phonePrimary.replace(/\s/g, "")}`} data-testid="footer-phone-primary" className="hover:text-[hsl(var(--brass))]">
                {SITE.phonePrimary}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Phone size={15} className="mt-0.5 text-[hsl(var(--brass))]" />
              <a href={`tel:${SITE.phoneSecondary.replace(/\s/g, "")}`} data-testid="footer-phone-secondary" className="hover:text-[hsl(var(--brass))]">
                {SITE.phoneSecondary}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[hsl(var(--bg))]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[hsl(var(--bg))]/60">
          <div>© {new Date().getFullYear()} The Barracks Bistro. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/login" data-testid="footer-login" className="hover:text-[hsl(var(--brass))]">Staff login</Link>
            <span>·</span>
            <span>Wellington, Nilgiris</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
